import { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import SharedHeader from './SharedHeader';
import Footer from './Footer';
import { Mail, Send, CheckCircle } from 'lucide-react';

const Contact = () => {
  const { language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const content = {
    en: {
      title: 'Contact Us',
      subtitle: 'Have questions or feedback? We\'d love to hear from you',
      form: {
        name: 'Your Name',
        email: 'Email Address',
        subject: 'Subject',
        message: 'Your Message',
        submit: 'Send Message',
        namePlaceholder: 'John Doe',
        emailPlaceholder: 'john@example.com',
        subjectPlaceholder: 'How can we help you?',
        messagePlaceholder: 'Tell us more about your question or feedback...'
      },
      successMessage: 'Thank you for your message! We\'ll get back to you soon.',
      contactInfo: {
        title: 'Other Ways to Reach Us',
        email: 'Email',
        emailAddress: 'support@qrcodeforge.com',
        response: 'We typically respond within 24-48 hours'
      },
      faq: {
        title: 'Quick Answers',
        items: [
          {
            q: 'How do I report a bug?',
            a: 'Please email us with details about the issue, including your browser and device information.'
          },
          {
            q: 'Can I suggest a new feature?',
            a: 'Absolutely! We love hearing feature suggestions from our users. Send us your ideas!'
          },
          {
            q: 'Do you offer business solutions?',
            a: 'Yes, we can discuss custom solutions for businesses. Contact us for more information.'
          }
        ]
      }
    },
    zh: {
      title: '联系我们',
      subtitle: '有问题或反馈？我们很乐意听取您的意见',
      form: {
        name: '您的姓名',
        email: '电子邮箱',
        subject: '主题',
        message: '您的留言',
        submit: '发送消息',
        namePlaceholder: '张三',
        emailPlaceholder: 'zhang@example.com',
        subjectPlaceholder: '我们如何帮助您？',
        messagePlaceholder: '告诉我们更多关于您的问题或反馈...'
      },
      successMessage: '感谢您的留言！我们会尽快回复您。',
      contactInfo: {
        title: '其他联系方式',
        email: '电子邮箱',
        emailAddress: 'support@qrcodeforge.com',
        response: '我们通常在24-48小时内回复'
      },
      faq: {
        title: '快速解答',
        items: [
          {
            q: '如何报告错误？',
            a: '请通过电子邮件向我们发送问题详情，包括您的浏览器和设备信息。'
          },
          {
            q: '我可以建议新功能吗？',
            a: '当然可以！我们喜欢听取用户的功能建议。请将您的想法发送给我们！'
          },
          {
            q: '你们提供商业解决方案吗？',
            a: '是的，我们可以讨论企业定制解决方案。请联系我们了解更多信息。'
          }
        ]
      }
    }
  };

  const currentContent = content[language];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real application, you would send this data to a backend
    console.log('Form submitted:', formData);
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: '', email: '', subject: '', message: '' });
    }, 3000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <SharedHeader />

      {/* 动态背景元素 */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -inset-10 opacity-30">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
          <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-1/4 left-1/3 w-64 h-64 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
        </div>
      </div>

      <div className="relative">
        {/* Header */}
        <header className="pt-20 pb-12">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <div className="flex items-center justify-center mb-6">
              <div className="p-3 bg-white/10 backdrop-blur-sm rounded-xl">
                <Mail className="w-8 h-8 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {currentContent.title}
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {currentContent.subtitle}
            </p>
          </div>
        </header>

        {/* Content */}
        <div className="max-w-6xl mx-auto px-4 pb-16">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8">
                {submitted ? (
                  <div className="text-center py-12">
                    <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-white mb-2">
                      {currentContent.successMessage}
                    </h3>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                        {currentContent.form.name}
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder={currentContent.form.namePlaceholder}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                        {currentContent.form.email}
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder={currentContent.form.emailPlaceholder}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-2">
                        {currentContent.form.subject}
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        placeholder={currentContent.form.subjectPlaceholder}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                        {currentContent.form.message}
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        placeholder={currentContent.form.messagePlaceholder}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors"
                    >
                      <Send className="w-4 h-4" />
                      {currentContent.form.submit}
                    </button>
                  </form>
                )}
              </div>
            </div>

            {/* Contact Info & FAQ */}
            <div className="space-y-6">
              {/* Contact Info */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-xl font-bold text-white mb-4">
                  {currentContent.contactInfo.title}
                </h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center gap-2 text-gray-300 mb-1">
                      <Mail className="w-4 h-4" />
                      <span className="font-medium">{currentContent.contactInfo.email}</span>
                    </div>
                    <a
                      href="mailto:support@qrcodeforge.com"
                      className="text-blue-300 hover:text-blue-200 text-sm"
                    >
                      {currentContent.contactInfo.emailAddress}
                    </a>
                  </div>
                  <p className="text-sm text-gray-400">
                    {currentContent.contactInfo.response}
                  </p>
                </div>
              </div>

              {/* Quick FAQ */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-xl font-bold text-white mb-4">
                  {currentContent.faq.title}
                </h3>
                <div className="space-y-4">
                  {currentContent.faq.items.map((item, index) => (
                    <div key={index}>
                      <h4 className="text-sm font-semibold text-white mb-1">
                        {item.q}
                      </h4>
                      <p className="text-sm text-gray-300">
                        {item.a}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
};

export default Contact;
